import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaSearch } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .rl-wrap {
    min-height: 100vh;
    background: #f0f2f8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    padding-top: 108px;
  }

  /* ── TOP BANNER ── */
  .rl-banner {
    background: linear-gradient(120deg, #1e1b4b 0%, #3730a3 50%, #4f46e5 100%);
    padding: 36px 48px 80px;
    position: relative;
    overflow: hidden;
  }

  .rl-banner::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 280px; height: 280px;
    border-radius: 50%;
    background: rgba(255,255,255,0.04);
  }

  .rl-banner::after {
    content: '';
    position: absolute;
    bottom: -80px; left: 30%;
    width: 360px; height: 360px;
    border-radius: 50%;
    background: rgba(255,255,255,0.03);
  }

  .rl-banner-title {
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.6px;
  }

  .rl-banner-sub {
    margin-top: 6px;
    font-size: 14px;
    color: rgba(255,255,255,0.55);
    font-weight: 400;
  }

  .rl-stats-row {
    display: flex;
    gap: 16px;
    margin-top: 24px;
  }

  .rl-stat-pill {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 40px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    display: flex;
    align-items: center;
    gap: 7px;
    backdrop-filter: blur(6px);
  }

  .rl-stat-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #818cf8;
  }

  /* ── FLOATING CONTENT PANEL ── */
  .rl-body {
    max-width: 1100px;
    margin: -44px auto 0;
    padding: 0 32px 60px;
    position: relative;
    z-index: 2;
  }

  /* ── ACTION BAR ── */
  .rl-actionbar {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(30,27,75,0.10);
    padding: 18px 22px;
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .rl-search-wrap {
    flex: 1;
    min-width: 180px;
    position: relative;
  }

  .rl-search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #a5b4fc;
    font-size: 13px;
  }

  .rl-search {
    width: 100%;
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px 10px 38px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .rl-search::placeholder { color: #c7c9e8; }

  .rl-search:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .rl-add-row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  .rl-new-input {
    border: 1.5px solid #e8eaf6;
    border-radius: 10px;
    padding: 10px 14px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    color: #1e1b4b;
    background: #f8f8fd;
    outline: none;
    width: 520px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .rl-new-input::placeholder { color: #c7c9e8; }

  .rl-new-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    background: #fff;
  }

  .rl-btn-add {
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
    box-shadow: 0 3px 12px rgba(79,70,229,0.35);
    transition: all 0.18s;
  }

  .rl-btn-add:hover {
    background: #4338ca;
    box-shadow: 0 5px 18px rgba(79,70,229,0.45);
    transform: translateY(-1px);
  }

  /* ── CARD GRID ── */
  .rl-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 16px;
  }

  .rl-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(30,27,75,0.07);
    padding: 22px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    border: 1.5px solid transparent;
    transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
    animation: cardIn 0.3s ease both;
  }

  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .rl-card:hover {
    box-shadow: 0 8px 28px rgba(79,70,229,0.13);
    border-color: #e0e1ff;
    transform: translateY(-2px);
  }

  .rl-card.editing {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  .rl-card-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    color: #4f46e5;
    letter-spacing: -1px;
    flex-shrink: 0;
  }

  .rl-card-name {
    font-size: 15px;
    font-weight: 700;
    color: #1e1b4b;
    line-height: 1.3;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  .rl-card-edit-input {
    width: 100%;
    border: 1.5px solid #6366f1;
    border-radius: 8px;
    padding: 8px 11px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    color: #1e1b4b;
    outline: none;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }

  .rl-card-footer {
    display: flex;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid #f0f1fb;
  }

  .rl-card-btn {
    flex: 1;
    border: none;
    border-radius: 8px;
    padding: 8px 0;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    transition: all 0.15s;
  }

  .rl-card-btn.edit-btn    { background: #f0f1ff; color: #4f46e5; }
  .rl-card-btn.edit-btn:hover    { background: #e0e3ff; }
  .rl-card-btn.del-btn     { background: #fff1f1; color: #ef4444; }
  .rl-card-btn.del-btn:hover     { background: #ffe0e0; }
  .rl-card-btn.save-btn    { background: #4f46e5; color: #fff; box-shadow: 0 2px 8px rgba(79,70,229,0.3); }
  .rl-card-btn.save-btn:hover    { background: #4338ca; }
  .rl-card-btn.cancel-btn  { background: #f3f4f6; color: #6b7280; }
  .rl-card-btn.cancel-btn:hover  { background: #e5e7eb; }

  /* ── EMPTY ── */
  .rl-empty {
    grid-column: 1 / -1;
    padding: 72px 0;
    text-align: center;
  }

  .rl-empty-emoji { font-size: 48px; margin-bottom: 14px; }
  .rl-empty-txt   { font-size: 15px; font-weight: 600; color: #818cf8; }
  .rl-empty-sub   { font-size: 13px; color: #c7c9e8; margin-top: 4px; }

  .rl-section-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #a5b4fc;
    margin-bottom: 14px;
  }

  @media (max-width: 640px) {
    .rl-banner { padding: 28px 20px 70px; }
    .rl-body   { padding: 0 14px 40px; }
    .rl-actionbar { flex-direction: column; align-items: stretch; }
    .rl-add-row   { flex-direction: column; }
    .rl-new-input { width: 100%; }
    .rl-banner-title { font-size: 22px; }
  }
`;

export default function RoleCRUD() {
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/all`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error();
      setRoles(await res.json());
    } catch { toast.error("Failed to load roles"); }
  };

  useEffect(() => { fetchRoles(); }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!t) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!storedRoles.includes("ROLE_ADMIN")) { toast.error("You are not authorized to access this page."); navigate("/"); }
  }, [navigate]);

  const handleAdd = async () => {
    if (!newRole.trim()) { toast.warning("Please enter a role name."); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newRole }),
      });
      if (res.ok) {
        toast.success("Role added successfully!");
        setNewRole("");
        fetchRoles();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error adding role."); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) { toast.warning("Please enter a valid name."); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/update/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: editName }),
      });
      if (res.ok) {
        toast.success("Role updated successfully!");
        setEditingId(null);
        fetchRoles();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error updating role."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.info("Role deleted.");
        fetchRoles();
      } else { toast.error(await res.text()); }
    } catch { toast.error("Error deleting role."); }
  };

  const initials = (name) => name ? name.slice(0, 2).toUpperCase() : "??";

  const filtered = roles.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="rl-wrap">
        {/* Banner */}
        <div className="rl-banner">
          <div className="rl-banner-title">Role Management</div>
          <div className="rl-banner-sub">Define and manage all user roles from one place</div>
          <div className="rl-stats-row">
            <div className="rl-stat-pill">
              <span className="rl-stat-dot" />
              {roles.length} Roles
            </div>
            {search && (
              <div className="rl-stat-pill">
                <span className="rl-stat-dot" style={{ background: "#fbbf24" }} />
                {filtered.length} Results
              </div>
            )}
          </div>
        </div>

        <div className="rl-body">
          {/* Action Bar */}
          <div className="rl-actionbar">
            <div className="rl-search-wrap">
              <FaSearch className="rl-search-icon" />
              <input
                className="rl-search"
                placeholder="Search roles…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="rl-add-row">
              <input
                className="rl-new-input"
                placeholder="New role name…"
                value={newRole}
                onChange={e => setNewRole(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleAdd()}
              />
              <button className="rl-btn-add" onClick={handleAdd}>
                <FaPlus size={11} /> Add
              </button>
            </div>
          </div>

          {/* Grid */}
          <div className="rl-section-label">All Roles</div>
          <div className="rl-grid">
            {filtered.length === 0 && (
              <div className="rl-empty">
                <div className="rl-empty-emoji">🛡️</div>
                <div className="rl-empty-txt">No roles found</div>
                <div className="rl-empty-sub">Try a different search or add one above</div>
              </div>
            )}
            {filtered.map((role) => (
              <div key={role.id} className={`rl-card${editingId === role.id ? " editing" : ""}`}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className="rl-card-icon">{initials(role.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {editingId === role.id ? (
                      <input
                        className="rl-card-edit-input"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") handleUpdate(role.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      <div className="rl-card-name">{role.name}</div>
                    )}
                  </div>
                </div>
                <div className="rl-card-footer">
                  {editingId === role.id ? (
                    <>
                      <button className="rl-card-btn save-btn" onClick={() => handleUpdate(role.id)}>
                        <FaSave size={10} /> Save
                      </button>
                      <button className="rl-card-btn cancel-btn" onClick={() => setEditingId(null)}>
                        <FaTimes size={10} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="rl-card-btn edit-btn"
                        onClick={() => { setEditingId(role.id); setEditName(role.name); }}>
                        <FaEdit size={10} /> Edit
                      </button>
                      <button className="rl-card-btn del-btn" onClick={() => handleDelete(role.id)}>
                        <FaTrash size={10} /> Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}